import React, { useEffect, useState } from 'react'
import { Typography, makeStyles, CircularProgress, Container } from '@material-ui/core'
import { HourglassEmpty, PauseCircleFilled, CheckCircle } from '@material-ui/icons'
import { useLocation } from 'react-router-dom'
import pollingSlice, {
  subscribeToEntityPollIds,
  subscribeToActivePollAmongIds,
  getActiveQuestionWithinPoll,
  getAnswersOfAttendeeForPoll,
  getPollById
} from 'store/polling'
import { useDispatch, useSelector } from 'react-redux'
import { Box } from '@mui/material'
import PollQuestionAttempt from 'views/admin/polling/public/PollQuestionAttempt'
import { POLLING_END_USER_MSGS, PollStatus } from 'constants/index'
import { colors } from 'utils/colors'

const PoolForm = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const attendeeId = queryParams.get('aid')
  const urlPollId = queryParams.get('pollId')
  const urlEventId = queryParams.get('eventId')
  const urlSessionId = queryParams.get('sessionId')
  const urlTalkId = queryParams.get('talkId')
  const { inProgress, poll, question } = useSelector((state) => state.polling)

  // Some join links (older mobile app screens, which can't be redeployed on short notice)
  // only ever send a bare pollId, with no event/session/talk scope at all. Start from
  // whatever scope the URL gives us, and — if it gave us none — look up that one poll and
  // adopt whichever entity it's currently attached to (see effect below), so we still watch
  // that entity's full pollIds set rather than being pinned to this one specific poll id
  // forever (which would miss any sibling poll, or a different one started later).
  const [scope, setScope] = useState({
    eventId: urlEventId,
    sessionId: urlSessionId,
    talkId: urlTalkId
  })
  const { eventId, sessionId, talkId } = scope

  useEffect(() => {
    if (urlEventId || urlSessionId || urlTalkId || !urlPollId) return
    const unsub = getPollById(urlPollId, (data) => {
      if (data?.talkId || data?.sessionId || data?.eventId) {
        setScope({
          eventId: data.eventId || null,
          sessionId: data.sessionId || null,
          talkId: data.talkId || null
        })
      }
    })
    return () => unsub && unsub()
  }, [urlEventId, urlSessionId, urlTalkId, urlPollId])

  const [entityPollIds, setEntityPollIds] = useState([])
  // The specific poll currently being watched. Only one of an entity's assigned polls can
  // be live at a time (enforced at start-time in PollView.js), so there's no fixed order to
  // step through — this just locks onto whichever one is active and switches once another
  // one takes over, while still tracking the current one's own lifecycle through to CLOSED.
  const [watchedPollId, setWatchedPollId] = useState(null)

  // Resolve the polls assigned to this talk/session/event join link. talkId takes priority
  // over sessionId over eventId, mirroring how each entity owns its own pollIds assignment
  // (see components/PollAssignment).
  useEffect(() => {
    const unsub = subscribeToEntityPollIds({ eventId, sessionId, talkId }, setEntityPollIds)
    return () => unsub && unsub()
  }, [eventId, sessionId, talkId])

  // Discover whichever of those polls is currently live, and lock onto it. setWatchedPollId
  // is a no-op re-render when the id hasn't changed, so this can set it unconditionally.
  useEffect(() => {
    const unsub = subscribeToActivePollAmongIds(entityPollIds, (activePoll) => {
      if (activePoll) setWatchedPollId(activePoll.id)
    })
    return () => unsub && unsub()
  }, [entityPollIds])

  // The pollId query param only matters for legacy direct-poll links that carry no
  // event/session/talk scope at all — if any of those are present, the entity owns the
  // resolution and pollId is ignored outright (not just once entityPollIds loads, since
  // that's async and would otherwise let a stale pollId briefly win right after mount).
  const hasEntityScope = Boolean(eventId || sessionId || talkId)
  const activePollId = watchedPollId ?? (hasEntityScope ? null : urlPollId)

  useEffect(() => {
    dispatch(pollingSlice.actions.setAnsweredBy(attendeeId))
    // Clear the outgoing poll's question/answers immediately so switching to the next poll
    // never briefly re-renders the previous (now concluded) poll's last question — the
    // render below only shows a question once it's confirmed to belong to the current poll.
    dispatch(pollingSlice.actions.setQuestion(null))
    dispatch(pollingSlice.actions.setAnswers([]))
    if (!activePollId) {
      dispatch(pollingSlice.actions.setPoll(null))
      dispatch(pollingSlice.actions.setProgress(false))
      return
    }
    dispatch(pollingSlice.actions.setProgress(true))
    const unsub = getPollById(activePollId, (data) => {
      dispatch(pollingSlice.actions.setPoll(data))
      dispatch(pollingSlice.actions.setProgress(false))
    })
    return () => unsub && unsub()
  }, [dispatch, activePollId])

  useEffect(() => {
    if (poll?.id && (poll?.status === PollStatus.STARTED || poll?.status === PollStatus.RESUMED)) {
      const unsubQuestion = getActiveQuestionWithinPoll(poll?.id, (data) => {
        dispatch(pollingSlice.actions.setQuestion(data))
      })
      return () => unsubQuestion && unsubQuestion()
    }
  }, [dispatch, poll?.id, poll?.status])

  useEffect(() => {
    if (
      question?.id &&
      attendeeId &&
      (poll?.status === PollStatus.STARTED || poll?.status === PollStatus.RESUMED)
    ) {
      const unsubAnswers = getAnswersOfAttendeeForPoll(poll?.id, attendeeId, (data) => {
        dispatch(pollingSlice.actions.setAnswers(data))
      })
      return () => unsubAnswers && unsubAnswers()
    }
  }, [dispatch, poll?.id, poll?.status, question?.id])

  if (inProgress) {
    return (
      <Box className={classes.page}>
        <CircularProgress style={{ color: colors.indigo }} />
      </Box>
    )
  } else if (!poll || (!poll?.isActive && poll?.status === PollStatus.CREATED)) {
    return (
      <InfoBox
        icon={HourglassEmpty}
        header={POLLING_END_USER_MSGS.inActive.header}
        subHeader={POLLING_END_USER_MSGS.inActive.subText}
      />
    )
  } else if (poll?.status === PollStatus.CREATED) {
    return (
      <InfoBox
        icon={HourglassEmpty}
        header={POLLING_END_USER_MSGS.toBegin.header}
        subHeader={POLLING_END_USER_MSGS.toBegin.subText}
      />
    )
  } else if (poll?.status === PollStatus.PAUSED) {
    return (
      <InfoBox
        icon={PauseCircleFilled}
        header={POLLING_END_USER_MSGS.toResume.header}
        subHeader={POLLING_END_USER_MSGS.toResume.subText}
      />
    )
  } else if (!poll?.isActive && poll?.status === PollStatus.CLOSED) {
    // If another poll for this event/session/talk goes live, the discovery effect above
    // will pick it up and switch away from this closed one automatically.
    return (
      <InfoBox
        icon={CheckCircle}
        header={POLLING_END_USER_MSGS.closed.header}
        subHeader={POLLING_END_USER_MSGS.closed.subText}
      />
    )
  } else if (!question || question.pollId !== poll.id) {
    // Poll has switched (STARTED/RESUMED) but its active question hasn't loaded yet —
    // avoids rendering the previous poll's stale question while this one catches up.
    return (
      <Box className={classes.page}>
        <CircularProgress style={{ color: colors.indigo }} />
      </Box>
    )
  } else {
    return (
      <Box className={classes.page}>
        <Container className={classes.container} maxWidth="sm">
          <PollQuestionAttempt question={question} />
        </Container>
      </Box>
    )
  }
}

export default PoolForm

export const InfoBox = ({ header, subHeader, icon: Icon = HourglassEmpty }) => {
  const classes = useStyles()
  return (
    <Box className={classes.page}>
      <Box className={classes.msgBox}>
        <Box className={classes.msgIconWrap}>
          <Icon className={classes.msgIcon} />
        </Box>
        <Typography className={classes.waitingHeader}>{header}</Typography>
        <Typography className={classes.waitingSubHeader}>{subHeader}</Typography>
      </Box>
    </Box>
  )
}

const useStyles = makeStyles((theme) => ({
  '@keyframes stateIn': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' }
  },
  page: {
    minHeight: '100vh',
    width: '100%',
    background: 'linear-gradient(180deg, #fbfcfe 0%, #eef1f7 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  container: {
    [theme.breakpoints.down('sm')]: {
      padding: '1.5rem 1rem'
    },
    paddingBottom: '1.5rem',
    paddingTop: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  msgBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '32px 28px',
    margin: '0 20px',
    maxWidth: '420px',
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    border: '1px solid rgba(15, 23, 42, 0.06)',
    boxShadow: '0 20px 45px -20px rgba(15, 23, 42, 0.18), 0 2px 8px rgba(15, 23, 42, 0.04)',
    animation: '$stateIn 0.4s ease'
  },
  msgIconWrap: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e3f6fa',
    color: '#0e7a94',
    marginBottom: '16px'
  },
  msgIcon: {
    fontSize: '30px'
  },
  waitingHeader: {
    fontWeight: 'bold',
    fontSize: '18px',
    color: '#0f172a'
  },
  waitingSubHeader: {
    fontSize: '14px',
    marginTop: '10px',
    color: '#64748b',
    lineHeight: '1.5'
  }
}))
